import CustomLoadingButton from 'Components/CustomLoadingButton'
import DateRangePicker from 'Components/DateRangePicker'
import MultiSelect, { type OptionType } from 'Components/MultiSelect/MultiSelect'
import StyledForm from 'Components/StyledForm'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import ReportDownload from 'Services/SalesManagement/Features/Report/Components/ReportDownload'
import ReportProgress from 'Services/SalesManagement/Features/Report/Components/ReportProgress'
import {
  createWebShopOrderReport,
  fetchWebShopOrderReport,
  ReportGenerationStatus,
  resetWebShopOrderReportWebsocket,
  WEB_SHOP_ORDER_REPORT_PATH,
  WEB_SHOP_ORDER_REPORT_PREFIX,
  type WebShopOrderReportParamShape
} from 'Services/SalesManagement/Features/Report/reportSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { dateFormatter } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import GenerateReportIcon from '@mui/icons-material/CloudUpload'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { type FormEvent, useState } from 'react'
import { t } from 'ttag'

export default function WebShopOrderReport() {
  const dispatch = useAppDispatch()
  const {
    webShopOrderReportLoading,
    singleWebShopOrderReport,
    webShopReportGenerationStatus,
    webShopReportGenerationTask
  } = useTypedSelector(state => state.report)

  const { authorities } = useAuthorityContext()

  const [params, setParams] = useState<WebShopOrderReportParamShape>({
    authorities: [],
    from_date: '',
    to_date: ''
  })

  const handleReportGenerate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Reset the report generation state before starting a new one
    dispatch(resetWebShopOrderReportWebsocket())
    const reportParams: WebShopOrderReportParamShape = { ...params }
    if (!reportParams.from_date) delete reportParams.from_date
    if (!reportParams.to_date) delete reportParams.to_date
    dispatch(createWebShopOrderReport(reportParams))
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Report generation successfully started.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error starting the report generation.`)
      })
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value).toString()
    }

    setParams(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const handleSelectChange = (selected: string[], name: keyof WebShopOrderReportParamShape) => {
    setParams(prevState => ({ ...prevState, [name]: selected }))
  }

  const authorityOptions: OptionType[] =
    authorities?.map(authority => [authority.code, authority.name]) ?? []

  const isLoading = webShopOrderReportLoading === 'pending'
  const isReportGenerationDisabled =
    webShopReportGenerationStatus?.status === ReportGenerationStatus.PENDING ||
    webShopReportGenerationStatus?.status === ReportGenerationStatus.PROGRESS

  return (
    <Paper sx={{ width: '100%', my: 2, p: 2, pt: 1 }}>
      <StyledForm onSubmit={handleReportGenerate} isDisabled={isLoading}>
        <Box sx={{ minWidth: 200, maxWidth: 400 }}>
          <MultiSelect
            handleChange={selected => handleSelectChange(selected, 'authorities')}
            label={t`Authority`}
            required={true}
            options={authorityOptions}
          />
        </Box>
        <Box>
          <DateRangePicker
            fromDate={params.from_date}
            toDate={params.to_date}
            onChange={handleDatePickerChange}
          />
        </Box>
        <Box>
          <CustomLoadingButton
            isLoading={isLoading}
            type="submit"
            disabled={isReportGenerationDisabled}
            icon={<GenerateReportIcon />}
            iconPosition="start"
          >{t`Generate report`}</CustomLoadingButton>
        </Box>
        <Box sx={{ my: 4 }}>
          <ReportProgress
            reportGenerationTask={webShopReportGenerationTask}
            reportPrefix={WEB_SHOP_ORDER_REPORT_PREFIX}
            reportStatus={webShopReportGenerationStatus}
            reportPath={WEB_SHOP_ORDER_REPORT_PATH}
            resetReportWebsocketState={() => {
              dispatch(resetWebShopOrderReportWebsocket())
            }}
          />
        </Box>
      </StyledForm>
      <ReportDownload
        singleReport={singleWebShopOrderReport}
        reportGenerationStatus={webShopReportGenerationStatus}
        fetchReport={reportName =>
          dispatch(fetchWebShopOrderReport({ report_name: reportName, ...params })).unwrap()
        }
        isLoading={isLoading}
      />
    </Paper>
  )
}
